import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  chatApiClient,
  ChatSearch,
  ChatSearchRequest,
  ChatSearchResponse,
} from '@src/api/shared/community/chatApiClient';
import { Pagination } from '@src/api/types/pagination';
import { ChatSearchSortOrder } from '@src/enum/chatSearchSortOrder';
import { addSearchHistory } from '@src/utils/searchHistoryUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useChatSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(() => searchParams.get('keyword') ?? '');
  const [searchResults, setSearchResults] = useState<ChatSearch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<ChatSearchSortOrder>(ChatSearchSortOrder.NEWEST);
  const isUserTypingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const channelSelectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const keyword = searchParams.get('keyword') ?? '';
  const page = parseInt(searchParams.get('page') ?? '1');

  const hasResults = searchResults.length > 0;
  const resultCount = pagination?.total || 0;

  useEffect(() => {
    if (!isUserTypingRef.current) {
      setSearchValue(keyword);
    }
  }, [keyword]);

  useEffect(() => {
    if (keyword) {
      performSearch(keyword, page, selectedChannels, sortOrder);
      addSearchHistory(keyword);
    } else {
      setSearchResults([]);
      setPagination(null);
    }
  }, [keyword, page, selectedChannels, sortOrder]);

  const performSearch = async (
    searchKeyword: string,
    currentPage: number = 1,
    channels: string[] = [],
    sort: ChatSearchSortOrder = ChatSearchSortOrder.NEWEST,
  ) => {
    if (typeof searchKeyword !== 'string' || !searchKeyword.trim()) {
      setSearchResults([]);
      setPagination(null);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const currentAbortController = new AbortController();
    abortControllerRef.current = currentAbortController;

    setIsLoading(true);

    try {
      const searchRequest: ChatSearchRequest = {
        keyword: searchKeyword.trim(),
        channel_codes: channels.length > 0 ? channels : undefined,
        sort_order: sort,
        page: currentPage,
        per_page: 20,
      };

      const result = await chatApiClient.search(searchRequest, currentAbortController.signal);

      if (result.result === 'aborted') {
        return;
      }

      if (currentAbortController.signal.aborted) {
        return;
      }

      handleApiResponse(result, (response: ChatSearchResponse) => {
        setSearchResults(response.data);
        setPagination(response.pagination);
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Search failed:', error);
      setSearchResults([]);
      setPagination(null);
    } finally {
      if (!currentAbortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const setSearchValueWithTyping = (value: string) => {
    isUserTypingRef.current = true;
    const stringValue = typeof value === 'string' ? value : '';
    setSearchValue(stringValue);
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 100);
  };

  const handleSearch = (searchTerm?: string) => {
    const termToSearch = searchTerm || searchValue || '';
    isUserTypingRef.current = false;

    if (typeof termToSearch !== 'string' || termToSearch.trim() === '') {
      setSearchParams(new URLSearchParams());
      setSearchResults([]);
      setPagination(null);
      return;
    }

    const params = new URLSearchParams();
    params.set('keyword', termToSearch);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearSearch = () => {
    isUserTypingRef.current = false;
    setSearchValue('');
    setSearchParams(new URLSearchParams());
    setSearchResults([]);
    setPagination(null);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleChannelSelection = useCallback((channels: string[]) => {
    if (channelSelectionTimeoutRef.current) {
      clearTimeout(channelSelectionTimeoutRef.current);
    }

    channelSelectionTimeoutRef.current = setTimeout(() => {
      setSelectedChannels(channels);
    }, 300);
  }, []);

  const handleSortOrderChange = (newSortOrder: ChatSearchSortOrder) => {
    setSortOrder(newSortOrder);
  };

  useEffect(() => {
    return () => {
      if (channelSelectionTimeoutRef.current) {
        clearTimeout(channelSelectionTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    searchValue,
    setSearchValue: setSearchValueWithTyping,
    keyword,
    searchResults,
    hasResults,
    resultCount,
    isLoading,
    pagination,
    selectedChannels,
    sortOrder,
    handleSearch,
    clearSearch,
    handlePageChange,
    handleChannelSelection,
    handleSortOrderChange,
  };
}
